import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import reportEvent from '../reportEvent';
import { visibility as propTypes } from '../propTypes';

/**
 * Callback function to run when the component becomes visible.
 * @param {Object} props The wrapped component's props.
 * @param {String} props.test The current test to check.
 * @param {Object} props.enrichedReportData Additional data to add to the report.
 * @param {Object} props.abTests User-allocated ab-tests.
 */
const onVisible = ({ test, enrichedReportData, abTests = {} }) => {
    const allocation = abTests[test] || 0;

    if (allocation > 0) {
        reportEvent({ experiment: test, group: allocation, enrichedReportData });
    }
};

/**
 * HOC for the Experiment component.
 * Adds automatic reporting when the component becomes visible.
 * @param  {React.Component} WrappedComponent The component to wrap.
 * @return {React.Component}
 */
const withExperimentReport = (WrappedComponent) => {
    const VisibilityWrapper = (props) => {
        const [ref, inView] = useInView({
            triggerOnce: true,
        });

        useEffect(() => {
            if (inView) {
                onVisible(props);
            }
        }, [inView, props]);

        return (
            <section ref={ref}>
                <WrappedComponent {...props} />
            </section>
        );
    };

    const ReportWrapper = ({ report, ...props }) => {
        const [isIntersectionObserverDefined, setIntersectionObserverDefined] = useState(false);

        useEffect(() => {
            setIntersectionObserverDefined(typeof IntersectionObserver !== 'undefined');
        }, []);

        return report && isIntersectionObserverDefined ? (
            <VisibilityWrapper {...props} />
        ) : (
            <WrappedComponent {...props} />
        );
    };

    ReportWrapper.propTypes = { ...propTypes };
    return ReportWrapper;
};

export default withExperimentReport;
